const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Delay (ms) after the page fully loads before force-revealing any sections
// that a user hasn't scroll-revealed yet, and before force-loading deferred
// media. Users who scroll within this window still get the on-scroll reveal
// animation; anything they didn't reach is revealed off-screen (invisible to
// them, so no UX cost). This guarantees the DOM reaches its final rendered
// state even for tools like Microsoft Clarity that reconstruct the page with
// our JS disabled and would otherwise capture unrevealed sections as blank.
const RENDER_FALLBACK_DELAY = 2500;

// Track scroll-reveal observers so the fallback can disconnect them and stop
// off-screen elements from being re-hidden after we force-reveal them.
const scrollAnimationObservers = [];

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -10px 0px',
  });
  scrollAnimationObservers.push(observer);
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Fallback: reveal every remaining scroll-trigger element regardless of scroll
// position. Disconnecting the observers first prevents onIntersection from
// re-adding the offscreen class once the element eventually scrolls into view.
function revealAllScrollTriggers() {
  while (scrollAnimationObservers.length) {
    scrollAnimationObservers.pop().disconnect();
  }
  document
    .querySelectorAll(`.${SCROLL_ANIMATION_TRIGGER_CLASSNAME}.${SCROLL_ANIMATION_OFFSCREEN_CLASSNAME}`)
    .forEach((element) => {
      // Add the cancel class so the element snaps to its visible end-state
      // (opacity:1, animation:none) instead of running the reveal keyframes.
      // Everything still offscreen here is out of view, so there's nothing to
      // animate for the user — this just avoids needless compositor work.
      element.classList.add(SCROLL_ANIMATION_CANCEL_CLASSNAME);
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    });
}

// Skip the bandwidth-heavy media fallback on metered or slow connections so we
// don't force downloads on the users most affected by them. The scroll-reveal
// fallback still runs — it costs no bandwidth.
function prefersReducedData() {
  const connection = navigator.connection;
  return Boolean(connection && (connection.saveData || /2g/.test(connection.effectiveType || '')));
}

// Fallback: force-load native lazy images that are never scrolled into view.
// loading="lazy" images far outside the viewport never fetch during heatmap
// reconstruction; flipping them to eager triggers the download. (Deferred
// .lazy-video elements have their own fallback in lazy-video.js.)
function loadDeferredImages() {
  document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    if (img.complete) return;
    img.loading = 'eager';
  });
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

// After the page is fully loaded, give real users a window to trigger the
// on-scroll animations, then guarantee the rest of the page renders so
// analytics/heatmap reconstructions capture the full layout. Skipped in the
// theme editor so design-mode behavior is unchanged.
if (!Shopify.designMode) {
  window.addEventListener('load', () => {
    setTimeout(() => {
      revealAllScrollTriggers();
      if (!prefersReducedData()) loadDeferredImages();
    }, RENDER_FALLBACK_DELAY);
  });
}

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
